import dynamic from "next/dynamic";
import useSWRInfinite from "swr/infinite";

import { useCallback, useEffect, useMemo, useState } from "react";
import InfiniteScroll from "react-swr-infinite-scroll";

import { uniqBy } from "lodash";
import numeral from "numeral";
import { twMerge } from "tailwind-merge";

import { faSort } from "@fortawesome/free-solid-svg-icons/faSort";
import { faSortDown } from "@fortawesome/free-solid-svg-icons/faSortDown";
import { faSortUp } from "@fortawesome/free-solid-svg-icons/faSortUp";

import { Loader } from "components/customElements/loader";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import Table from "components/Table";
import useAuthentication from "hooks/useAuthentication";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";

const AccountUpgradeForm = dynamic(
  () => import("components/AccountUpgradeForm")
);

export const shareholdersUrl = (id: string, page: number, sort?: string) => {
  const url = new ApiEndpoint(`/api/v2/entities/${id}/share-registries`);
  url.searchParams.append("include", "institution");
  url.searchParams.append("sort", sort || "-total-holding");
  url.searchParams.append("page[number]", `${page}`);
  url.searchParams.append("page[size]", "12");
  return url.href;
};

interface Props {
  entity: any;
}

const Shareholder = ({ entity }: Props) => {
  const { account, isAccountLoading } = useAuthentication();

  const [sort, setSort] = useState("-total-holding");
  const { data, isValidating, mutate, size, setSize } = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (previousPageData?.length === 0) return null;
      if (
        !(
          account?.isProfessionalClient ||
          account?.isInsightProvider ||
          account?.isInternalUser
        ) &&
        pageIndex + 1 > 2
      ) {
        return null;
      }
      return shareholdersUrl(entity?.id, pageIndex + 1, sort);
    },
    baseFetcher
  );
  const totalPages = data?.[size - 1]?.meta?.totalPages;
  const hasMore = totalPages > size;

  const tableData = useMemo(() => {
    const items =
      data
        ?.reduce((acc, page) => {
          if (page?.data) return [...acc, ...page.data];
          return acc;
        }, [])
        ?.filter((i: any) => !i.isHidden) || [];

    return uniqBy(items, (item: any) => item.id);
  }, [data]);

  const onSortHeader = useCallback(
    (key) => {
      if (sort.includes(key) && sort.startsWith("-")) {
        setSort(sort.replace("-", ""));
      } else {
        setSort(`-${key}`);
      }
    },
    [sort]
  );

  const tableHeaderRender = useCallback(
    (title: string, key: string) => {
      return (
        <div className="flex items-center" onClick={() => onSortHeader(key)}>
          <span className="line-clamp-1">{title}</span>
          <FontAwesomeIcon
            icon={
              sort.includes(key)
                ? sort.startsWith("-")
                  ? faSortDown
                  : faSortUp
                : faSort
            }
            className={twMerge(
              sort.includes(key)
                ? sort.startsWith("-")
                  ? "mt-[-4px]"
                  : "mb-[-4px]"
                : ""
            )}
          />
        </div>
      );
    },
    [onSortHeader, sort]
  );

  const tableColumns = [
    {
      key: "holder",
      title: "Name (Type)",
      dataIndex: "name",
      style: {
        minWidth: "200px",
      },
      render: (text: string, record: any) => {
        if (!record?.institution?.id) {
          return <span className="line-clamp-1">{text}</span>;
        }
        return (
          <div className="flex items-center gap-[10px] line-clamp-1 group-hover:text-smart-teal group-hover:underline">
            <span className="line-clamp-1 underline md:no-underline">
              {text}
            </span>
          </div>
        );
      },
      renderHeader: (title: string) => tableHeaderRender(title, "name"),
    },
    {
      key: "percentage",
      title: "Holding",
      dataIndex: "totalHolding",
      style: {},
      render: (text: string) => numeral(text).format("0.0a"),
      renderHeader: (title: string) =>
        tableHeaderRender(title, "total-holding"),
    },
    {
      key: "shares",
      title: "Shares",
      dataIndex: "holdingPercentage",
      style: {},
      render: (text: string) => numeral(text).format("0,0.00%"),
      renderHeader: (title: string) =>
        tableHeaderRender(title, "holding-percentage"),
    },
    {
      key: "change",
      title: "Change",
      dataIndex: "changeInPercentage",
      style: {},
      render: (text: string) => numeral(text).format("0,0.00%"),
      renderHeader: (title: string) =>
        tableHeaderRender(title, "change-in-percentage"),
    },
    {
      key: "latest-date",
      title: "Disclosed",
      dataIndex: "reportDate",
      style: {},
      renderHeader: (title: string) => tableHeaderRender(title, "report-date"),
    },
  ];

  useEffect(() => {
    if (!hasMore) mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isAccountLoading) return <Loader />;

  if (!account?.canViewEntityShareholders) {
    return (
      <>
        <div className="bg-mellow-yellow rounded-xl p-[15px] text-subtle-black">
          As a {account?.accountType} holder, you do not have access to Top
          Shareholders. Upgrade now to get full access.
        </div>
        <div className="mt-[25px]">
          <AccountUpgradeForm />
        </div>
      </>
    );
  }

  return (
    <>
      <InfiniteScroll
        swr={{ data, mutate, isValidating, size, setSize }}
        isReachingEnd={!hasMore}
        endingIndicator={<div className={""}></div>}
      >
        <div className="mb-[30px] overflow-x-auto">
          <Table
            columns={tableColumns}
            dataSource={tableData || []}
            rowClassNameCallback={(record) => {
              if (!record?.institution?.id) return "";
              return "hover:!bg-nearly-white cursor-pointer group";
            }}
            headRowClassName="cursor-pointer group"
            onRowClick={(record) => {
              if (!record?.institution?.id) return;
              window.open(`/institutions/${record?.institution?.id}`, "_blank");
            }}
          />
          {isValidating && <Loader />}
        </div>
      </InfiniteScroll>
    </>
  );
};

export default Shareholder;
